import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { placeFloating, type FloatSide } from "../lib/floating";

interface FloatingOptions {
  /** `vertical` flips below/above (menus, dropdowns); `horizontal` flips right/left (submenus). */
  axis?: "vertical" | "horizontal";
  align?: "start" | "end";
  gap?: number;
  margin?: number;
  /** The panel is never narrower than its trigger — dropdowns that replace a `<select>`. */
  matchAnchorWidth?: boolean;
  /** Called when the anchor has scrolled out of sight, so the panel can close itself. */
  onDismiss?: () => void;
  /** Measure against someone else's element instead of the returned `anchorRef`. */
  anchor?: RefObject<HTMLElement | null>;
  /**
   * Changes whenever the anchor becomes a *different* element while the panel
   * stays open. Without it, moving a submenu from one row to the next keeps the
   * first row's coordinates: `open` never went false, so nothing re-measures.
   */
  anchorKey?: string | number | null;
}

/**
 * Pins a floating panel to a trigger, in `position: fixed` viewport coordinates.
 *
 * Fixed + a portal is the whole point: a menu on the last row of a scrolling
 * list used to be clipped by its own `overflow` ancestor, and no z-index fixes
 * that. Geometry lives in `lib/floating.ts`; this hook only measures, listens
 * and writes the style.
 */
export function useFloatingPanel<
  A extends HTMLElement = HTMLElement,
  P extends HTMLElement = HTMLDivElement,
>(open: boolean, options: FloatingOptions = {}) {
  const {
    axis = "vertical",
    align = "end",
    gap = 4,
    margin = 8,
    matchAnchorWidth = false,
    onDismiss,
    anchor: external,
    anchorKey = null,
  } = options;

  const anchorRef = useRef<A | null>(null);
  const panelRef = useRef<P | null>(null);
  const [placed, setPlaced] = useState<CSSProperties | null>(null);
  const [side, setSide] = useState<FloatSide>(axis === "vertical" ? "bottom" : "right");
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  const reposition = useCallback(() => {
    const anchor = external?.current ?? anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const rect = anchor.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      dismiss.current?.();
      return;
    }
    // scrollHeight + borders rather than offsetHeight: once a maxHeight has been
    // applied the element measures its clamped self, and the panel would shrink
    // a little more on every scroll tick.
    const natural = {
      width: panel.scrollWidth + (panel.offsetWidth - panel.clientWidth),
      height: panel.scrollHeight + (panel.offsetHeight - panel.clientHeight),
    };
    const wanted = matchAnchorWidth ? Math.max(natural.width, rect.width) : natural.width;
    const next = placeFloating({
      anchor: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      panel: { width: wanted, height: natural.height },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      axis,
      align,
      gap,
      margin,
    });

    // Written straight to the node as well as to state. A nested panel measures
    // its anchor *inside* this one, and a React state write lands a frame later
    // — which is exactly one frame of the submenu trailing its parent on every
    // scroll tick. The state keeps React's idea of the style in sync.
    panel.style.position = "fixed";
    panel.style.left = `${next.left}px`;
    panel.style.top = `${next.top}px`;
    panel.style.maxHeight = `${next.maxHeight}px`;
    panel.style.maxWidth = `${next.maxWidth}px`;
    panel.style.visibility = "visible";
    if (matchAnchorWidth) panel.style.minWidth = `${rect.width}px`;

    setSide(next.side);
    setPlaced({
      position: "fixed",
      left: next.left,
      top: next.top,
      maxHeight: next.maxHeight,
      maxWidth: next.maxWidth,
      minWidth: matchAnchorWidth ? rect.width : undefined,
    });
  }, [axis, align, gap, margin, matchAnchorWidth, external]);

  useLayoutEffect(() => {
    if (!open) {
      setPlaced(null);
      return;
    }
    reposition();
  }, [open, anchorKey, reposition]);

  useEffect(() => {
    if (!open) return;
    let frame = 0;
    // One measure per frame: scroll fires far faster than the panel can move.
    const handle = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        reposition();
      });
    };
    // Capture phase so a scrolling *container* counts, not just the page.
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    // The panel's own size can change while it stays open — a confirm row
    // swapping in a longer label, a list filtered by typeahead.
    const observer = new ResizeObserver(handle);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
      observer.disconnect();
    };
  }, [open, anchorKey, reposition]);

  return {
    anchorRef,
    panelRef,
    side,
    /** Hidden until measured, so nothing flashes at the top-left corner. */
    style: placed ?? { position: "fixed", top: 0, left: 0, visibility: "hidden" as const },
  };
}
